import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, RefreshCw, PackageCheck, AlertTriangle, Package, ChevronDown, ChevronRight } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface BundleComponent {
  product_name: string;
  quantity: number;
}

interface SyncedItem {
  product_name: string;
  delivery_date: string;
  net_quantity_delivered?: number;
  total_quantity?: number;
  total_quantity_sold?: number;
  return_quantity?: number;
  price?: number;
  order_type?: string;
  measurement?: string;
  measuring_unit?: string;
  selected: boolean;
  is_bundle?: boolean;
  bundle_components?: BundleComponent[];
  is_duplicate?: boolean;
}

export function ManualSyncTrigger() {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [b2bLoading, setB2bLoading] = useState(false);
  const [chipchipLoading, setChipchipLoading] = useState(false);
  const [processingInventory, setProcessingInventory] = useState(false);
  const [b2bItems, setB2bItems] = useState<SyncedItem[]>([]);
  const [chipchipItems, setChipchipItems] = useState<SyncedItem[]>([]);
  const [processResult, setProcessResult] = useState<any>(null);
  const [expandedBundles, setExpandedBundles] = useState<Set<string>>(new Set());

  // Fetch bundles for preview matching
  const { data: bundlesData } = useQuery({
    queryKey: ['product-bundles-for-sync'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_bundles')
        .select('id, bundle_name, is_active, product_bundle_items(product_id, quantity, products:product_id(name))')
        .eq('is_active', true);
      if (error) throw error;
      return data;
    },
  });

  const getBundleMap = () => {
    const map: Record<string, BundleComponent[]> = {};
    if (bundlesData) {
      for (const b of bundlesData) {
        map[b.bundle_name.toLowerCase().trim()] = (b.product_bundle_items || []).map((item: any) => ({
          product_name: item.products?.name || 'Unknown',
          quantity: item.quantity,
        }));
      }
    }
    return map;
  };

  const triggerSync = async (type: 'b2b' | 'chipchip') => {
    const fnName = type === 'b2b' ? 'b2b-delivery-sync' : 'chipchip-accepted-orders';
    const setLoading = type === 'b2b' ? setB2bLoading : setChipchipLoading;
    const setItems = type === 'b2b' ? setB2bItems : setChipchipItems;

    setLoading(true);
    setProcessResult(null);

    try {
      const { data, error } = await supabase.functions.invoke(fnName, {
        body: { startDate, endDate, skipInventory: true },
      });

      if (error) throw error;

      const records = data?.data || [];
      const bundleMap = getBundleMap();

      const items: SyncedItem[] = records
        .filter((r: any) => {
          const qty = type === 'b2b' ? r.net_quantity_delivered : r.total_quantity;
          return qty > 0;
        })
        .map((r: any) => {
          const normalizedName = r.product_name.toLowerCase().trim();
          const bundleComponents = bundleMap[normalizedName];
          const qty = type === 'b2b' ? r.net_quantity_delivered : r.total_quantity;

          let scaledComponents: BundleComponent[] | undefined;
          if (bundleComponents) {
            const totalBundleWeight = bundleComponents.reduce((s, c) => s + c.quantity, 0);
            const bundleCount = totalBundleWeight > 0 ? qty / totalBundleWeight : 1;
            scaledComponents = bundleComponents.map(c => ({
              product_name: c.product_name,
              quantity: c.quantity * bundleCount,
            }));
          }

          return {
            product_name: r.product_name,
            delivery_date: r.delivery_date,
            net_quantity_delivered: r.net_quantity_delivered,
            total_quantity: r.total_quantity,
            total_quantity_sold: r.total_quantity_sold,
            return_quantity: r.return_quantity,
            price: r.price,
            order_type: r.order_type,
            measurement: r.measurement,
            measuring_unit: r.measuring_unit,
            selected: true,
            is_bundle: !!bundleComponents,
            bundle_components: scaledComponents,
          };
        });

      // Check for duplicates by building reference numbers and querying inventory_transactions
      const referenceNumbers = items.map(item => {
        const prefix = type === 'b2b' ? 'B2B' : 'ACCEPTED';
        return `${prefix}-${item.delivery_date}-${item.product_name.replace(/\s+/g, '_')}`;
      });

      if (referenceNumbers.length > 0) {
        const { data: existingTxs } = await supabase
          .from('inventory_transactions')
          .select('reference_number')
          .in('reference_number', referenceNumbers);

        const existingSet = new Set((existingTxs || []).map((tx: any) => tx.reference_number));

        items.forEach((item, idx) => {
          const isDuplicate = existingSet.has(referenceNumbers[idx]);
          item.is_duplicate = isDuplicate;
          if (isDuplicate) item.selected = false; // auto-deselect duplicates
        });
      }

      setItems(items);
      const dupCount = items.filter(i => i.is_duplicate).length;
      toast.success(`${type === 'b2b' ? 'B2B' : 'ChipChip'}: ${items.length} items fetched (${dupCount} already recorded)`);
      queryClient.invalidateQueries({ queryKey: ['sync-summary'] });
    } catch (err: any) {
      toast.error(`Sync failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (type: 'b2b' | 'chipchip', index: number) => {
    const setter = type === 'b2b' ? setB2bItems : setChipchipItems;
    setter(prev => prev.map((item, i) =>
      i === index ? { ...item, selected: !item.selected } : item
    ));
  };

  const toggleAll = (type: 'b2b' | 'chipchip', checked: boolean) => {
    const setter = type === 'b2b' ? setB2bItems : setChipchipItems;
    setter(prev => prev.map(item => ({ ...item, selected: checked })));
  };

  const toggleBundleExpand = (key: string) => {
    setExpandedBundles(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const processInventory = async () => {
    setProcessingInventory(true);
    setProcessResult(null);

    try {
      const selectedB2b = b2bItems
        .filter(i => i.selected)
        .map(i => ({
          product_name: i.product_name,
          quantity: i.net_quantity_delivered || 0,
          delivery_date: i.delivery_date,
          source: 'b2b' as const,
          total_quantity_sold: i.total_quantity_sold,
          return_quantity: i.return_quantity,
        }));

      const selectedChipchip = chipchipItems
        .filter(i => i.selected)
        .map(i => ({
          product_name: i.product_name,
          quantity: i.total_quantity || 0,
          delivery_date: i.delivery_date,
          source: 'chipchip' as const,
          price: i.price,
          order_type: i.order_type,
        }));

      const allItems = [...selectedB2b, ...selectedChipchip];

      if (allItems.length === 0) {
        toast.warning('No items selected for inventory entry');
        return;
      }

      const { data, error } = await supabase.functions.invoke('process-sync-inventory', {
        body: { items: allItems },
      });

      if (error) throw error;

      setProcessResult(data);
      toast.success(`Inventory updated: ${data.processed} entries, ${data.skippedDuplicates} duplicates skipped`);

      queryClient.invalidateQueries({ queryKey: ['sync-summary'] });
      queryClient.invalidateQueries({ queryKey: ['sync-daily-volumes'] });
      queryClient.invalidateQueries({ queryKey: ['b2b-sync-details'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-sync-details'] });
      queryClient.invalidateQueries({ queryKey: ['unmatched-products'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-stock'] });
    } catch (err: any) {
      toast.error(`Processing failed: ${err.message}`);
    } finally {
      setProcessingInventory(false);
    }
  };

  const totalSelected = b2bItems.filter(i => i.selected).length + chipchipItems.filter(i => i.selected).length;
  const hasPreviewData = b2bItems.length > 0 || chipchipItems.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Manual Sync Trigger
        </CardTitle>
        <CardDescription>
          Fetch delivery data, review products, then confirm which ones to record as inventory outbound. Bundles are automatically expanded into component products.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div>
            <label className="text-sm text-muted-foreground">Start Date</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-sm text-muted-foreground">End Date</label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button onClick={() => triggerSync('b2b')} disabled={b2bLoading}>
            {b2bLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Fetch B2B
          </Button>
          <Button onClick={() => triggerSync('chipchip')} disabled={chipchipLoading} variant="secondary">
            {chipchipLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Fetch ChipChip
          </Button>
        </div>

        {hasPreviewData && (
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                Review before confirming — uncheck items you don't want to record
              </h4>
              <Badge variant="outline">{totalSelected} selected</Badge>
            </div>

            {b2bItems.length > 0 && (
              <PreviewTable
                title="B2B Delivery"
                items={b2bItems}
                type="b2b"
                onToggle={(i) => toggleItem('b2b', i)}
                onToggleAll={(checked) => toggleAll('b2b', checked)}
                expandedBundles={expandedBundles}
                onToggleBundleExpand={toggleBundleExpand}
              />
            )}

            {chipchipItems.length > 0 && (
              <PreviewTable
                title="ChipChip Orders"
                items={chipchipItems}
                type="chipchip"
                onToggle={(i) => toggleItem('chipchip', i)}
                onToggleAll={(checked) => toggleAll('chipchip', checked)}
                expandedBundles={expandedBundles}
                onToggleBundleExpand={toggleBundleExpand}
              />
            )}

            <Button
              onClick={processInventory}
              disabled={processingInventory || totalSelected === 0}
              className="w-full"
              size="lg"
            >
              {processingInventory ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <PackageCheck className="h-4 w-4 mr-2" />
              )}
              Confirm & Record {totalSelected} Inventory Entries
            </Button>
          </div>
        )}

        {processResult && (
          <div className="mt-4 p-4 rounded-lg bg-muted text-sm space-y-2">
            <p className="font-medium">Processing Result</p>
            <div className="flex gap-3 flex-wrap">
              <Badge variant="default">{processResult.processed} recorded</Badge>
              <Badge variant="secondary">{processResult.skippedDuplicates} duplicates skipped</Badge>
              <Badge variant="outline">{processResult.skippedNoMatch} no product match</Badge>
            </div>
            {processResult.results && (
              <ScrollArea className="h-40 mt-2">
                <div className="space-y-1">
                  {processResult.results.map((r: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <Badge
                        variant={r.status === 'success' ? 'default' : r.status === 'duplicate' ? 'secondary' : 'destructive'}
                        className="text-[10px] px-1.5"
                      >
                        {r.status}
                      </Badge>
                      <span>{r.product_name}</span>
                      {r.detail && <span className="text-muted-foreground">— {r.detail}</span>}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PreviewTable({
  title,
  items,
  type,
  onToggle,
  onToggleAll,
  expandedBundles,
  onToggleBundleExpand,
}: {
  title: string;
  items: SyncedItem[];
  type: 'b2b' | 'chipchip';
  onToggle: (index: number) => void;
  onToggleAll: (checked: boolean) => void;
  expandedBundles: Set<string>;
  onToggleBundleExpand: (key: string) => void;
}) {
  const allSelected = items.every(i => i.selected);
  const someSelected = items.some(i => i.selected) && !allSelected;

  return (
    <div className="border rounded-lg">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={allSelected}
            // @ts-ignore
            indeterminate={someSelected}
            onCheckedChange={(checked) => onToggleAll(!!checked)}
          />
          <span className="font-medium text-sm">{title}</span>
          <Badge variant="outline" className="text-xs">{items.length}</Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          {items.filter(i => i.selected).length} / {items.length} selected
        </span>
      </div>
      <ScrollArea className="h-64">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Quantity</TableHead>
              {type === 'b2b' && <TableHead className="text-right">Sold</TableHead>}
              {type === 'b2b' && <TableHead className="text-right">Return</TableHead>}
              {type === 'chipchip' && <TableHead className="text-right">Price</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item, i) => {
              const bundleKey = `${type}-${i}`;
              const isExpanded = expandedBundles.has(bundleKey);
              return (
                <>
                  <TableRow key={i} className={!item.selected ? 'opacity-40' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={item.selected}
                        onCheckedChange={() => onToggle(i)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.is_bundle && (
                          <button onClick={() => onToggleBundleExpand(bundleKey)} className="p-0.5">
                            {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                          </button>
                        )}
                        <span className="font-medium text-sm">{item.product_name}</span>
                        {item.is_bundle && (
                          <Badge variant="default" className="text-[10px] px-1.5 bg-purple-600">
                            <Package className="h-3 w-3 mr-0.5" /> Bundle
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{item.delivery_date}</TableCell>
                    <TableCell>
                      {item.is_duplicate ? (
                        <Badge variant="secondary" className="text-[10px] px-1.5">Recorded</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 border-green-500 text-green-600">New</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {type === 'b2b'
                        ? `${item.net_quantity_delivered?.toFixed(1)} kg`
                        : `${item.total_quantity?.toFixed(1)} kg`
                      }
                    </TableCell>
                    {type === 'b2b' && (
                      <TableCell className="text-right text-sm">{item.total_quantity_sold}</TableCell>
                    )}
                    {type === 'b2b' && (
                      <TableCell className="text-right text-sm">{item.return_quantity}</TableCell>
                    )}
                    {type === 'chipchip' && (
                      <TableCell className="text-right text-sm">{item.price?.toFixed(2)}</TableCell>
                    )}
                  </TableRow>
                  {item.is_bundle && isExpanded && item.bundle_components?.map((comp, ci) => (
                    <TableRow key={`${i}-comp-${ci}`} className="bg-muted/30">
                      <TableCell></TableCell>
                      <TableCell className="text-sm pl-10 text-muted-foreground">↳ {comp.product_name}</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">{comp.quantity.toFixed(1)} kg</TableCell>
                      {type === 'b2b' && <TableCell></TableCell>}
                      {type === 'b2b' && <TableCell></TableCell>}
                      {type === 'chipchip' && <TableCell></TableCell>}
                    </TableRow>
                  ))}
                </>
              );
            })}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
